import { gsap } from "gsap";
import Flip from 'gsap/Flip';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(Flip, ScrollTrigger);

import SearchModal from '../utils/search.js';
import CartDrawer from '../utils/cart-drawer.js';
import FlipButton from '../snippets/flipbutton';

export default class Header {
    constructor(container) {
        this.container = container;
        this.header_hamburger = this.container.querySelector('.header__hamburger');
        this.header_menu = this.container.querySelector('.header__menu');
        this.isActive = false;

        this.menu_items = this.container.querySelectorAll('.header__menu-item--has-dropdown');
        this.menu_buttons = this.container.querySelectorAll('button.header__menu-link');
        this.mega_menus = this.container.querySelectorAll('.mega-menu');
        this.menu_extras = this.container.querySelectorAll('.with-love, .social, .header__menu-secondary');

        this.mm = gsap.matchMedia();
        // cache commonly-used inner elements for scoped animations
        this.hamburger_line_1 = this.header_hamburger && this.header_hamburger.querySelector('._1');
        this.hamburger_line_2 = this.header_hamburger && this.header_hamburger.querySelector('._2');
        this.select = gsap.utils.selector(this.container);

        // Initialize search modal (independent component)
        this.searchModal = new SearchModal();
        this.searchButton = this.container.querySelector('[data-search-trigger]');

        // Initialize cart drawer (independent component)
        this.cartDrawer = new CartDrawer();
        this.cartButton = this.container.querySelector('[data-cart-trigger]');

        this.flipbuttons = this.container.querySelectorAll('.js-flipbutton');

        this.init();
        this.events();
    }

    init = () => {
        // SplitText FlipButtons are hover-only; skip them in the hamburger so
        // opening the menu isn't paying for char splits on every card title.
        if (window.matchMedia('(min-width: 1024px)').matches) {
            this.flipbuttons.forEach((element) => new FlipButton(element, { from: 'center', amount: 0, fade: false }));
        }

        this.initFooterHide();
    }

    // .shopify-section { overflow: hidden } breaks CSS sticky; the header is
    // position:fixed, so slide it off before it overlaps the footer.
    // Measured from the end of the scroll range rather than the footer's
    // layout position: pinned / overlapping sections (about page) make the
    // footer's layout top sit far above where it visually appears, which
    // hid the bar much too early.
    initFooterHide = () => {
        const footer = document.querySelector('.shopify-section-group-footer-group');
        if (!footer) return;

        const LEAD = 120; // px of footer already in view before the bar hides
        const root = document.documentElement;
        const bar = document.querySelector('.shopify-section-group-header-group');

        ScrollTrigger.getById('header-footer-hide')?.kill();

        const update = (self) => {
            const maxScroll = ScrollTrigger.maxScroll(window);
            const remaining = maxScroll - self.scroll();
            let hide = remaining < footer.offsetHeight - LEAD;

            // Short pages (a policy page with a few paragraphs) have the footer
            // in view from the start, so "near the scroll end" is true at
            // scroll 0 and the bar vanished on load. When the whole scroll
            // range is shorter than the footer, hide only once the footer's
            // rendered top actually reaches the bar.
            if (hide && maxScroll < footer.offsetHeight) {
                hide = footer.getBoundingClientRect().top <= (bar?.offsetHeight || 0);
            }

            root.classList.toggle('footer-visible', hide);
        };

        ScrollTrigger.create({
            id: 'header-footer-hide',
            start: 0,
            end: 'max',
            onUpdate: update,
            onRefresh: update,
        });
    }

    events = () => {
        this.mm.add("(max-width: 1023px)", () => {
            gsap.set(this.header_menu, {
                autoAlpha: 1,
                clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
            });
        });
        this.mm.add("(min-width: 1024px)", () => {
            gsap.set([this.header_menu, this.menu_items], { clearProps: true });
        });
        this.menu_items.forEach(element => {
            element.addEventListener('mouseenter', this.menu_item_onMouseEnter);
            element.addEventListener('mouseleave', this.menu_item_onMouseLeave);
        });

        this.menu_buttons.forEach(element => {
            element.addEventListener('click', this.menu_button_onClick);
        });

        this.header_hamburger.addEventListener('click', this.hamburger_onClick);

        // Search button
        if (this.searchButton && this.searchModal) {
            this.searchButton.addEventListener('click', () => {
                this.searchModal.open();
            });
        }

        // Cart button
        if (this.cartButton && this.cartDrawer) {
            this.cartButton.addEventListener('click', () => {
                this.cartDrawer.open();
            });
        }
    }

    menu_item_onMouseEnter = () => {
        if (window.matchMedia('(max-width: 1023px)').matches) return;
        document.documentElement.classList.add('menu-item---hovered')
    }
    menu_item_onMouseLeave = () => {
        if (window.matchMedia('(max-width: 1023px)').matches) return;
        document.documentElement.classList.remove('menu-item---hovered')
    }

    hamburger_onClick = (e) => {
        gsap.killTweensOf(this.header_menu);
        if (!this.isActive) {
            this.isActive = true;
            this.container.classList.add('active');
            document.body.classList.add('menu-open');
            this.set_menu_extras_hidden(false);
            // The whole header (bar + menu) is the scroll container on
            // mobile/tablet; keep Lenis off it and start from the top.
            this.set_sheet_scrolling(true);
            this.reset_sheet_scroll('instant');

            if (this.hamburger_line_1) gsap.to(this.hamburger_line_1, { scale: 0.8, rotate: 45, y: -9, duration: 0.4, ease: 'power2.out', transformOrigin: '0% 50%' });
            if (this.hamburger_line_2) gsap.to(this.hamburger_line_2, { scale: 0.8, rotate: -45, y: 9, duration: 0.4, ease: 'power2.out', transformOrigin: '0% 50%' });

            gsap.to(this.header_menu, { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', duration: 1, delay: 0.2, ease: 'power2.out', transformOrigin: '50% 0%' });
            gsap.fromTo(this.menu_items, { autoAlpha: 0, x: -10 }, { autoAlpha: 1, x: 0, duration: 0.7, ease: 'power2.out', delay: 0.4, stagger: { each: 0.1 } });
        } else {
            this.isActive = false;

            if (this.hamburger_line_1) gsap.to(this.hamburger_line_1, { scale: 1, rotate: 0, y: 0, duration: 0.4, ease: 'power2.out' });
            if (this.hamburger_line_2) gsap.to(this.hamburger_line_2, { scale: 1, rotate: 0, y: 0, duration: 0.4, ease: 'power2.out' });

            // The sheet now grows with an open submenu, so the menu box is no
            // longer a fixed 100dvh: a plain 100% -> 0% wipe would spend most
            // of the tween below the fold. Start it at the bottom edge of what
            // is actually on screen instead (100% when the menu fits).
            const wipe_from = this.visible_menu_bottom_pct();

            gsap.fromTo(this.header_menu, {
                clipPath: `polygon(0% 0%, 100% 0%, 100% ${wipe_from}%, 0% ${wipe_from}%)`,
            }, {
                clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)', duration: 1, ease: 'power2.in', transformOrigin: '50% 0%', onComplete: () => {
                    this.container.scrollTop = 0;
                    this.set_sheet_scrolling(false);
                    this.container.classList.remove('active');
                    document.body.classList.remove('menu-open');
                    this.remove_active();
                    this.set_menu_extras_hidden(false);
                }
            });
        }
    }

    menu_button_onClick = (e) => {
        // Only run this behavior on mobile sizes; avoid adding a new mm rule per click
        if (!window.matchMedia('(max-width: 1023px)').matches) return;

        const button = e.currentTarget;
        const parent = button.parentElement;
        const mega_menu = parent.querySelector('.mega-menu');
        const $parent = gsap.utils.selector(parent);
        const tl = gsap.timeline();

        // Switching level re-renders the sheet contents; never leave the user
        // parked at an old scroll offset.
        this.reset_sheet_scroll('smooth');

        if (!button.classList.contains('active')) {
            this.remove_active();
            parent.classList.add('active');
            this.set_menu_extras_hidden(true);
            tl.set(this.container.querySelectorAll('.header__menu-item--has-dropdown:not(.active)'), { autoAlpha: 0 });
            tl.call(() => {
                button.classList.add('active');
                mega_menu.classList.add('active');
            });
            tl.set($parent('.header__menu-arrow__mobile'), { rotate: 180, transformOrigin: '50% 50%' });
            tl.to($parent('.header__menu-arrow__mobile'), { scale: 1, duration: 0.7, ease: 'expo.out', transformOrigin: '50% 50%' });
            tl.to($parent('.header__menu-label'), { x: 35, duration: 0.7, ease: 'expo.out' }, '<');

            tl.fromTo($parent('.mega-menu__columns>*'), { autoAlpha: 0 }, { autoAlpha: 1, duration: 1, stagger: { each: 0.07 }, ease: 'power3.out' }, '<0.2');

        } else {
            tl.to($parent('.header__menu-arrow__mobile'), { scale: 0, duration: 0.7, ease: 'expo.out', transformOrigin: '50% 50%' });
            tl.set($parent('.header__menu-arrow__mobile'), { rotate: 0, transformOrigin: '50% 50%' }, '<');
            tl.to($parent('.header__menu-label'), { x: 0, duration: 0.7, ease: 'expo.out' }, '<');
            tl.call(() => {
                parent.classList.remove('active');
                button.classList.remove('active');
                mega_menu.classList.remove('active');
                this.set_menu_extras_hidden(false);
            }, null, '<');
            tl.to(this.menu_items, { autoAlpha: 1, duration: 0, stagger: { each: 0.02 }, ease: 'expo.out' }, '<');
            tl.to($parent('.header__menu-arrow__mobile'), { scale: 1, duration: 0.7, ease: 'expo.out', transformOrigin: '50% 50%' }, '<0.3');
        }
    }

    remove_active = () => {
        this.menu_items.forEach(element => {
            element.classList.remove('active');
            gsap.set(element, { clearProps: true })
            element.querySelectorAll('.header__menu-arrow__mobile, .header__menu-label').forEach(item => {
                gsap.set(item, { clearProps: true })
            });
        });
        this.menu_buttons.forEach(element => {
            element.classList.remove('active');
        });
        this.mega_menus.forEach(element => {
            element.classList.remove('active');
        });
    }

    // How far down the menu box the viewport currently reaches, as a
    // percentage of its height. 100 when the menu is no taller than the sheet.
    visible_menu_bottom_pct = () => {
        const rect = this.header_menu.getBoundingClientRect();
        if (!rect.height) return 100;

        const pct = ((window.innerHeight - rect.top) / rect.height) * 100;
        return Math.min(100, Math.max(0, Math.round(pct * 100) / 100));
    }

    // On <=1023px `.header.active` is a 100dvh scroll container holding both
    // the bar row and the menu. Lenis must not also scroll the page while the
    // user drags inside it.
    set_sheet_scrolling = (enabled) => {
        if (enabled) {
            if (!window.matchMedia('(max-width: 1023px)').matches) return;
            this.container.setAttribute('data-lenis-prevent', '');
        } else {
            this.container.removeAttribute('data-lenis-prevent');
        }
    }

    reset_sheet_scroll = (behavior = 'instant') => {
        if (!this.isActive) return;
        if (!window.matchMedia('(max-width: 1023px)').matches) return;
        if (this.container.scrollTop === 0) return;

        if (typeof this.container.scrollTo === 'function') {
            this.container.scrollTo({ top: 0, behavior });
        } else {
            this.container.scrollTop = 0;
        }
    }

    set_menu_extras_hidden = (hidden) => {
        if (!this.menu_extras.length) return;

        gsap.set(this.menu_extras, hidden ? { display: 'none' } : { clearProps: 'display' });
    }
}
